import type { ThrottleConfig } from './errorThrottle.config'

// Minimal storage surface so the throttle can run against localStorage in the
// browser and an in-memory map in SSR/tests. Backend is swappable without
// touching call sites.
export interface ThrottleStorage {
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => void
  removeItem: (key: string) => void
  keys: () => string[]
}

export interface ThrottleDeps {
  now: () => number
  storage: ThrottleStorage
}

interface FingerprintEntry {
  count: number
  mutedUntil: number
  lastSeen: number
}

interface GlobalEntry {
  windowStart: number
  count: number
}

const PREFIX = 'errbus:v1:'
const GLOBAL_KEY = `${PREFIX}__global__`
// Cap on stored fingerprints; oldest are pruned first so a long tail of unique
// errors can't grow localStorage without bound.
const MAX_KEYS = 200

const read = <T>(storage: ThrottleStorage, key: string): T | null => {
  const raw = storage.getItem(key)
  if (!raw) return null
  try {
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

const write = (storage: ThrottleStorage, key: string, value: unknown): void => {
  try {
    storage.setItem(key, JSON.stringify(value))
  } catch {
    // Quota or private-mode failures must never break error reporting.
  }
}

const fingerprintKey = (fingerprint: string) => `${PREFIX}${fingerprint}`

// Drop fingerprint entries whose cooldown has elapsed and that haven't been seen
// recently; if still over budget, evict the oldest by lastSeen.
const prune = (storage: ThrottleStorage, now: number): void => {
  const entryKeys = storage.keys().filter((k) => k.startsWith(PREFIX) && k !== GLOBAL_KEY)
  if (entryKeys.length < MAX_KEYS) return

  // Single read pass: drop entries past their cooldown, keep the rest as survivors.
  const survivors: { key: string; entry: FingerprintEntry }[] = []
  for (const key of entryKeys) {
    const entry = read<FingerprintEntry>(storage, key)
    if (entry === null || entry.mutedUntil <= now) {
      storage.removeItem(key)
    } else {
      survivors.push({ key, entry })
    }
  }

  if (survivors.length < MAX_KEYS) return

  // Still over budget — evict the oldest survivors by lastSeen.
  survivors
    .sort((a, b) => a.entry.lastSeen - b.entry.lastSeen)
    .slice(0, survivors.length - MAX_KEYS + 1)
    .forEach(({ key }) => storage.removeItem(key))
}

/**
 * Burst + cooldown throttle, shared across tabs via the injected storage.
 *
 * Per fingerprint: the first `burstCap` occurrences send, then it is muted for
 * `cooldownMs`; once the cooldown elapses the entry resets and counting resumes.
 * A global rolling-window backstop caps total sends across all fingerprints.
 *
 * Returns true when the error should be forwarded.
 */
export const shouldSend = (fingerprint: string, config: ThrottleConfig, deps: ThrottleDeps): boolean => {
  if (!config.enabled) return true

  const { now: nowFn, storage } = deps
  const now = nowFn()
  const key = fingerprintKey(fingerprint)

  const stored = read<FingerprintEntry>(storage, key)
  const fresh: FingerprintEntry = { count: 0, mutedUntil: 0, lastSeen: now }
  // A *finished* cooldown (mutedUntil set and now past) resets the entry so a
  // recurring error starts a fresh burst. mutedUntil === 0 means never muted.
  const cooldownElapsed = stored !== null && stored.mutedUntil > 0 && stored.mutedUntil <= now
  const entry: FingerprintEntry = stored === null || cooldownElapsed ? fresh : stored

  entry.lastSeen = now

  if (entry.mutedUntil > now) {
    write(storage, key, entry)

    return false
  }

  // Global backstop: only counts actual sends, so it never blocks the first
  // sighting of a brand-new error unless the whole app is already flooding.
  const globalStored = read<GlobalEntry>(storage, GLOBAL_KEY)
  const global: GlobalEntry =
    globalStored && now - globalStored.windowStart <= config.globalWindowMs
      ? globalStored
      : { windowStart: now, count: 0 }

  if (global.count >= config.globalMax) {
    write(storage, key, entry)

    return false
  }

  entry.count += 1
  if (entry.count >= config.burstCap) entry.mutedUntil = now + config.cooldownMs

  global.count += 1

  prune(storage, now)
  write(storage, key, entry)
  write(storage, GLOBAL_KEY, global)

  return true
}

// localStorage-backed storage; falls back to an in-memory map when unavailable
// (SSR, private mode, disabled storage) so the throttle degrades gracefully.
export const createInMemoryStorage = (): ThrottleStorage => {
  const map = new Map<string, string>()

  return {
    getItem: (k) => map.get(k) ?? null,
    setItem: (k, v) => void map.set(k, v),
    removeItem: (k) => void map.delete(k),
    keys: () => [...map.keys()],
  }
}

export const createDefaultStorage = (): ThrottleStorage => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return createInMemoryStorage()
    const ls = window.localStorage
    // Probe access — Safari private mode throws on setItem.
    const probe = `${PREFIX}__probe__`
    ls.setItem(probe, '1')
    ls.removeItem(probe)

    return {
      getItem: (k) => ls.getItem(k),
      setItem: (k, v) => ls.setItem(k, v),
      removeItem: (k) => ls.removeItem(k),
      keys: () => Object.keys(ls),
    }
  } catch {
    return createInMemoryStorage()
  }
}
